<template>
  <div
    v-if="view"
    class="control__btn menu-btn"
    title="Table of contents"
    @click="view.show()"
  >
    <div class="control__icon-box">
      <span class="control__icon iconfont icontrol-menu"></span>
    </div>
    <transition name="fade">
      <div
        v-show="view.isVisible"
        class="control-menu"
        @click.stop="view.clickOutside($event)"
      >
        <transition name="book-toc" appear>
          <!-- `v-show` is added for the following `div` to enable Vue transition -->
          <div
            v-show="view.isVisible"
            class="control-menu__content"
            :class="{ 'control-menu__content--active': view.isVisible }"
          >
            <a class="control-menu__close" @click.stop="view.hide()">
              <span
                class="control-menu__close-icon iconfont isummary-close"
              ></span>
            </a>
            <div class="control-menu__backlink-box">
              <router-link
                replace
                class="control-menu__backlink"
                :to="previewPage"
              >
                <span class="control-menu__backlink-icon iconfont iprev"></span>
                Back to preview page
              </router-link>
            </div>
            <ul class="control-menu__list">
              <li
                :class="view.onePageSummaryWrapperCss()"
                class="control-menu__unit control-menu__one-page"
              >
                <router-link
                  replace
                  :to="view.onePageSummaryLink()"
                  class="control-menu__link"
                  @click="view.menuItemClick()"
                >
                  <span
                    :class="view.onePageSummaryCss()"
                    class="control-menu__icon iconfont"
                  ></span>
                  <span>{{ doc.pages[0].title }}</span>
                </router-link>
              </li>

              <Expandable
                v-if="view.hasFullSummary()"
                :icon-class="view.fullSummaryCss()"
                :title="doc.fullSummaryItemText"
                :default-open="true"
                class="control-menu__full-summary"
              >
                <li
                  v-for="page in doc.pages.slice(1)"
                  :key="page.order"
                  class="control-menu__sub-unit"
                  :class="view.chapterWrapperCss(page)"
                >
                  <router-link
                    replace
                    :to="view.chapterLink(page)"
                    class="control-menu__sub-link"
                    @click.stop="view.menuItemClick()"
                  >
                    <span
                      class="control-menu__icon iconfont"
                      :class="view.chapterCss(page)"
                    ></span>
                    <span>{{ page.title }}</span>
                  </router-link>
                </li>
              </Expandable>

              <li
                class="control-menu__unit"
                :class="{
                  'control-menu__unit--active': doc.isCurrentHighlights(),
                }"
              >
                <router-link
                  replace
                  :to="{
                    name: doc.routeNames().highlights,
                    params: { url_slug: doc.url_slug },
                  }"
                  class="control-menu__link"
                >
                  <span
                    class="control-menu__icon iconfont isummary-highlights"
                  ></span>
                  <span>Highlights and Notes</span>
                </router-link>
              </li>

              <Expandable
                icon-class="iflag control-menu__icon-bookmark"
                title="Bookmarks"
                class="control-menu__unit-bookmarks"
              >
                <div
                  v-if="doc.bookmarks.length === 0"
                  class="control-menu__bookmarks-empty"
                >
                  <p class="control-menu__bookmarks-empty-text">
                    Your bookmarks will appear here. You can create a bookmark
                    by selecting some text and clicking the
                    <span class="control-menu__icon iconfont iflag"></span>
                    icon.
                  </p>
                </div>
                <li
                  v-for="bookmark in doc.bookmarks"
                  v-else
                  :key="bookmark.id"
                  class="control-menu__sub-unit control-menu__bookmarks"
                >
                  <router-link
                    replace
                    :to="view.bookmarkLink(bookmark)"
                    class="control-menu__sub-link"
                    @click.stop="view.menuItemClick()"
                    @click="view.bookmarkClick(bookmark)"
                  >
                    <span
                      class="control-menu__icon iconfont control-menu__icon-bookmark iflag"
                    ></span>
                    <span class="control-menu__bookmarks-text">{{
                      bookmark.quote
                    }}</span>
                  </router-link>
                </li>
              </Expandable>

              <li
                class="control-menu__unit"
                :class="{
                  'control-menu__unit--active': doc.isCurrentCommunity(),
                }"
              >
                <router-link
                  replace
                  :to="{
                    name: doc.routeNames().community,
                    params: { url_slug: doc.url_slug },
                  }"
                  class="control-menu__link"
                >
                  <span
                    class="control-menu__icon iconfont isummary-community"
                  ></span>
                  <span>Discussion</span>
                </router-link>
              </li>

              <NativeStorageDownloadButton
                :native-storage-view="nativeStorageView"
              />
              <li v-if="nativeStorageView?.error" class="control-menu__unit">
                <span class="download-error">{{
                  nativeStorageView.error
                }}</span>
              </li>
            </ul>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, Raw, ref } from 'vue'

import { DocTocView } from '@/models/doc.toc'
import { BookNativeStorageView } from '@/models/book.native.storage.view'
import { DocView } from '@/models/doc'

import NativeStorageDownloadButton from '@/app/components/doc/NativeStorageDownloadButton.vue'
import Expandable from './Expandable.vue'

const props = defineProps<{
  doc: DocView
}>()

const view = ref<DocTocView | null>(null)
const nativeStorageView = ref<Raw<BookNativeStorageView> | null>(null)

onBeforeMount(async () => {
  view.value = new DocTocView(props.doc)
  await view.value.init()
  nativeStorageView.value = await BookNativeStorageView.initialize(props.doc)
})

const previewPage = computed(() => {
  return {
    name: props.doc.routeNames().preview,
    params: {
      url_slug: props.doc.url_slug,
    },
  }
})
</script>
